
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';

interface PrivateRouteProps {
    allowedRoles?: string[];
    children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ allowedRoles, children }) => {
    const { user, loading, userProfile, userRoles } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    
    // THE FIX: If the user is currently on the registration page, do not redirect them.
    // This allows the registration process to complete and prevents the infinite loop.
    if (location.pathname === '/register') {
        return <>{children}</>;
    }

    if (!userProfile || !userProfile.profileComplete) {
        return <Navigate to="/register" state={{ from: location }} replace />;
    }

    // Role-based access control check
    if (allowedRoles && allowedRoles.length > 0) {
        const hasRequiredRole = userRoles.some(role => allowedRoles.includes(role));
        if (!hasRequiredRole) {
            // --- DIAGNOSTIC LOGGING ---
            console.error("Access Denied: User does not have the required roles.", {
                requiredRoles: allowedRoles,
                userRoles: userRoles,
                path: location.pathname
            });
            // --- END DIAGNOSTIC LOGGING ---
            return (
                 <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Typography variant="h4">Access Denied</Typography>
                    <Typography>You do not have the necessary permissions to view this page. Check the console for more details.</Typography>
                </Box>
            );
        }
    }

    return <>{children}</>;
};

export default PrivateRoute;
